// src/oura/screens/Skills.tsx
//
// The Skill library (B13, scope 2.3): an Oura staff screen at /oura/skills,
// not part of the shared Settings page. 1 slot per catalogue doc type: the
// skill Create a doc would run for it, what Oura would send it, its version
// and who added it, and the spend cap skill runs share.
//
// ourteam supplies the skills, so a slot is filled when ourteam adds one. Create
// a doc then runs it (#3983, ourteam-backend#5268), and each slot lists its last
// 5 runs with who started them, when and the cost once measured, and the
// server's sentence for a run that failed or stopped at the cap. Runs read
// "None yet" and cost per run "Not measured", never 0; the cap is YW's default
// and is labelled a placeholder. "How a skill is added" says how one gets here:
// no upload. The route and the rail entry show only where DOCS_WRITE_ON.
//
//   Head     "Skill library", the count up front, and How a skill is added.
//   Tiles    Skills added · Skill runs · Cost per run.
//   Bento    Doc types (1 line per slot, grouped No skill yet / Added; a row
//            opens its slot in the drawer) beside Spend cap and Where a
//            skill plugs in.
//   Drawer   A slot: what Oura will send it, the slot's facts, where it plugs
//            in; previous and next walk the doc types. Or How a skill is added.

import { useEffect, useState, type ReactNode } from "react";
import { CircleDollarSignIcon, FileTextIcon, Loader2Icon, PlugIcon, PlusIcon, SparklesIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { DOCS_WRITE_ON, getSkillLibrary, type SkillLibrary, type SkillSlot } from "@service/aeo/clientDocsWrite";
import type { DocsFailure } from "@service/aeo/clientDocs";
import { cn } from "../lib/cn";
import {
    Bento,
    BentoStack,
    Drawer,
    DrawerBlock,
    DrawerFoot,
    DrawerHead,
    FamilyChip,
    FoldCard,
    FoldScope,
    GroupFold,
    IconSquare,
    ListRow,
    PageHead,
    PanelIcon,
    PillButton,
    Steps,
    Surface,
    SURFACE,
    TYPE,
    Tag,
    Tile,
    Tiles,
    drawerNav,
    drawerSequence,
} from "../ui";
import { codeFamily, docsFailureText, plural } from "../docs";
import { OURA_ROOT } from "../nav";
import { capText, spentText } from "../components/docs/create/createModel";
import { costText, runError, runMeta, runStatus, type RunTone } from "../docs/skillRun";

type Load = { state: "loading" } | { state: "ready"; data: SkillLibrary } | { state: "failed"; error: DocsFailure };

/** Which sheet the drawer holds: a slot by its doc type, or How a skill is added. */
type Open = { kind: "slot"; docType: string } | { kind: "add" } | null;

function CodeChips({ codes }: { codes: readonly string[] }) {
    return (
        <>
            {codes.map((c) => (
                <FamilyChip key={c} family={codeFamily(c)}>
                    {c}
                </FamilyChip>
            ))}
        </>
    );
}

/** A fact line in a drawer block: its label, then its value. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className={cn("flex items-start justify-between gap-3 border-t py-2 first:border-t-0 first:pt-0", SURFACE.hairline)}>
            <span className={cn(TYPE.tc, "shrink-0 text-gray-500")}>{label}</span>
            <span className={cn(TYPE.tb, "min-w-0 text-right text-gray-900")}>{children}</span>
        </div>
    );
}

const PLACEHOLDER = <Tag tone="gray">Placeholder</Tag>;

const RUN_TONE_TEXT: Record<RunTone, string> = {
    gray: "text-gray-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
};

function SlotSheet({ slot, cap, onDone }: { slot: SkillSlot; cap: SkillLibrary["cap"]; onDone: () => void }) {
    const added = slot.status === "added";
    return (
        <>
            <DrawerHead
                lead={<IconSquare tone={added ? "green" : "gray"} icon={FileTextIcon} />}
                leadKind="square"
                title={slot.docType}
                chips={<CodeChips codes={slot.codes} />}
                status={
                    added
                        ? { tone: "green", label: "Skill added", clock: slot.version ? { text: `v${slot.version}` } : null }
                        : { tone: "gray", label: "No skill yet", clock: { text: `Sends ${plural(slot.inputs.length, "fact")}` } }
                }
            />
            <DrawerBlock label={`What Oura will send it · ${slot.inputs.length}`}>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>A skill runs with no internet, so Oura sends it everything it needs.</p>
                <div role="list" className="mt-2">
                    {slot.inputs.map((i) => (
                        <div role="listitem" key={i.key} className={cn("flex items-start justify-between gap-3 border-t py-2 first:border-t-0", SURFACE.hairline)}>
                            <span className="min-w-0 text-gray-900">{i.label}</span>
                            <span className={cn(TYPE.tc, "shrink-0 text-gray-500")}>{i.from}</span>
                        </div>
                    ))}
                </div>
            </DrawerBlock>
            <DrawerBlock label="The slot">
                <div className="mt-1">
                    <Fact label="Version">{slot.version ? `v${slot.version}` : "None yet"}</Fact>
                    <Fact label="Added by">{slot.addedBy ?? "Nobody yet"}</Fact>
                    <Fact label="Cost per run">{slot.costPerRunUsd === null ? "Not measured" : `$${slot.costPerRunUsd.toFixed(2)}`}</Fact>
                    <Fact label="Runs">{slot.runs.length === 0 ? "None yet" : plural(slot.runs.length, "run")}</Fact>
                    <Fact label="Monthly cap">
                        <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            {capText(cap)}
                            {PLACEHOLDER}
                        </span>
                    </Fact>
                </div>
            </DrawerBlock>
            {/* The last runs for this doc type, any client's (the server lists 5). A failed run
                or one stopped at the cap says why in the server's own sentence (#3983); a
                cost that was not measured is left out, never printed as $0. */}
            <DrawerBlock label={`Last runs · ${slot.runs.length}`}>
                {slot.runs.length === 0 ? (
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>None yet.</p>
                ) : (
                    <ul className="m-0 list-none p-0" aria-label="Last runs">
                        {slot.runs.map((r) => {
                            const status = runStatus(r);
                            const error = runError(r);
                            return (
                                <li key={r.id} className={cn("border-t py-2 first:border-t-0 first:pt-0", SURFACE.hairline)}>
                                    <p className={cn("m-0 font-medium", RUN_TONE_TEXT[status.tone])}>{status.label}</p>
                                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{runMeta(r)}</p>
                                    {r.note && <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-700")}>Note: {r.note}</p>}
                                    {error && <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-700")}>{error}</p>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </DrawerBlock>
            <DrawerBlock label="Where it plugs in">
                <p className="m-0 text-gray-900">
                    Create a doc offers &ldquo;Write it with the skill&rdquo; for the {slot.docType} once a skill is here. A person reads the doc before it is shared.
                </p>
            </DrawerBlock>
            <DrawerFoot message={{ tone: "info", text: "ourteam adds each skill to Oura's own workspace." }} first={<PillButton onClick={onDone}>Done</PillButton>} />
        </>
    );
}

function AddSheet({ onDone }: { onDone: () => void }) {
    return (
        <>
            <DrawerHead lead={<PanelIcon icon={PlusIcon} area="ai" size={16} />} title="How a skill is added" status={{ tone: "gray", label: "ourteam supplies the skills" }} />
            <DrawerBlock>
                <p className="m-0 text-gray-900">Each skill is written for 1 doc type.</p>
            </DrawerBlock>
            <DrawerBlock label="How a skill gets here">
                <div className="mt-2">
                    <Steps
                        items={[
                            { key: "write", title: "ourteam writes the skill", meta: "For 1 doc type, to run with no internet", state: "next" },
                            { key: "add", title: "It goes into Oura's own Anthropic workspace", meta: "Under the monthly cap", state: "later" },
                            { key: "slot", title: "Its slot here shows the version and who added it", meta: "Create a doc then offers Write it with the skill", state: "later" },
                        ]}
                    />
                </div>
            </DrawerBlock>
            <DrawerFoot first={<PillButton onClick={onDone}>Done</PillButton>} />
        </>
    );
}

export function Skills() {
    const [load, setLoad] = useState<Load>({ state: "loading" });
    const [attempt, setAttempt] = useState(0);
    const [open, setOpen] = useState<Open>(null);

    useEffect(() => {
        let live = true;
        setLoad({ state: "loading" });
        void getSkillLibrary().then((r) => {
            if (live) setLoad(r.ok ? { state: "ready", data: r.data } : { state: "failed", error: r });
        });
        return () => {
            live = false;
        };
    }, [attempt]);

    if (!DOCS_WRITE_ON) {
        return (
            <FoldScope value="oura-skills">
                <PageHead title="Skill library" />
                <Surface>
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>The Skill library isn&rsquo;t on here yet.</p>
                </Surface>
            </FoldScope>
        );
    }

    const addButton = (
        <PillButton variant="text" onClick={() => setOpen({ kind: "add" })}>
            How a skill is added
        </PillButton>
    );

    if (load.state !== "ready") {
        return (
            <FoldScope value="oura-skills">
                <PageHead title="Skill library" actions={addButton} />
                {load.state === "loading" ? (
                    <div role="status" aria-label="Loading the Skill library" className="flex justify-center py-24">
                        <Loader2Icon aria-hidden className="h-6 w-6 animate-spin text-gray-400 motion-reduce:animate-none" />
                    </div>
                ) : (
                    <Surface>
                        <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn&rsquo;t load the Skill library</h2>
                        <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>{docsFailureText(load.error, "the Skill library")}</p>
                        {load.error.reason !== "notServed" && (
                            <PillButton className="mt-4 self-start" onClick={() => setAttempt((n) => n + 1)}>
                                Try again
                            </PillButton>
                        )}
                    </Surface>
                )}
                <Drawer open={open?.kind === "add"} onClose={() => setOpen(null)} itemKey="add">
                    {open?.kind === "add" && <AddSheet onDone={() => setOpen(null)} />}
                </Drawer>
            </FoldScope>
        );
    }

    const { slots, cap } = load.data;
    const none = slots.filter((s) => s.status !== "added");
    const added = slots.filter((s) => s.status === "added");
    const order = [...none, ...added].map((s) => s.docType);
    const openSlot = open?.kind === "slot" ? slots.find((s) => s.docType === open.docType) ?? null : null;
    const seq = openSlot ? drawerSequence(order, openSlot.docType, (id) => id) : null;
    const nav = seq ? drawerNav(seq, { group: "Doc types", onMove: (id) => setOpen({ kind: "slot", docType: id }), keys: true }) : undefined;

    // The runs the library lists (the last 5 per doc type), and the cost per run where one was measured.
    const listedRuns = slots.reduce((n, s) => n + s.runs.length, 0);
    const measuredCosts = slots.map((s) => s.costPerRunUsd).filter((c): c is number => typeof c === "number");
    const perRun = measuredCosts.length > 0 ? costText(measuredCosts.reduce((a, b) => a + b, 0) / measuredCosts.length) : null;

    const sentence = added.length === 0
        ? `No skill added yet. ${plural(slots.length, "doc type")} ${slots.length === 1 ? "waits" : "wait"} for one.`
        : `${added.length} of ${plural(slots.length, "doc type")} ${added.length === 1 ? "has" : "have"} a skill.`;

    const row = (s: SkillSlot) => (
        <ListRow
            key={s.docType}
            lead="square"
            icon={<IconSquare tone={s.status === "added" ? "green" : "gray"} icon={FileTextIcon} />}
            title={s.docType}
            tags={<CodeChips codes={s.codes} />}
            meta={s.status === "added" && s.version ? `v${s.version}` : `Sends ${plural(s.inputs.length, "fact")}`}
            onClick={() => setOpen({ kind: "slot", docType: s.docType })}
            muted={s.status !== "added"}
            stackMeta
            srStatus={s.status === "added" ? "Skill added" : "No skill yet"}
            data-skill-slot={s.docType}
        />
    );

    return (
        <FoldScope value="oura-skills">
            <PageHead
                title="Skill library"
                // Fixed, as every Oura page head now is (#4026 C-FE-6).
                sentence="The skills Oura can run for a client, and their versions."
                status={sentence}
                actions={addButton}
            />

            <div className="space-y-5">
                <Tiles cols={3} compactOnPhone>
                    <Tile compactOnPhone label="Skills added" icon={{ icon: SparklesIcon, area: "ai" }} value={`${added.length} of ${slots.length}`} sub="ourteam adds each one" />
                    <Tile
                        compactOnPhone
                        label="Skill runs"
                        icon={{ icon: CircleDollarSignIcon, area: "dates" }}
                        value={listedRuns === 0 ? "None yet" : plural(listedRuns, "run")}
                        sub={added.length === 0 ? "No skill can run yet" : "The last 5 for each doc type"}
                    />
                    <Tile
                        compactOnPhone
                        label="Cost per run"
                        icon={{ icon: CircleDollarSignIcon, area: "dates" }}
                        value={perRun === null ? "Not measured" : perRun}
                        sub={perRun === null ? "Measured on the first real run" : "Averaged over the doc types with a measured run"}
                    />
                </Tiles>

                <Bento layout="c21">
                    <FoldCard id="skills.slots" title="Doc types" icon={{ icon: FileTextIcon, area: "setup" }} foldedFact={`${none.length} with no skill yet`}>
                        <div>
                            {none.length > 0 && (
                                <GroupFold id="skills.none" lead="square" tone="gray" title="No skill yet" count={none.length}>
                                    <div>{none.map(row)}</div>
                                </GroupFold>
                            )}
                            {added.length > 0 && (
                                <GroupFold id="skills.added" lead="square" tone="green" title="Skill added" count={added.length}>
                                    <div>{added.map(row)}</div>
                                </GroupFold>
                            )}
                        </div>
                    </FoldCard>

                    <BentoStack>
                        <FoldCard id="skills.cap" title="Spend cap" icon={{ icon: CircleDollarSignIcon, area: "dates" }} foldedFact={`$${cap.monthCapUsd} a month, a placeholder`}>
                            <div className={SURFACE.inner}>
                                <Fact label="Skill runs">{`$${cap.monthCapUsd} a month`}</Fact>
                                <Fact label="Alert at">{`$${cap.alertAtUsd}`}</Fact>
                                <Fact label="This month">{spentText(cap)}</Fact>
                                {/* Moved here from the doc drawer: a cost cap is a setting, not part of a working screen. */}
                                <Fact label="AI summaries of docs">$20 a month, alert at $16</Fact>
                            </div>
                            <p className={cn(TYPE.tc, "m-0 mt-3 flex flex-wrap items-center gap-2 text-gray-500")}>
                                {PLACEHOLDER}
                                <span>The default until a real run is measured. Runs stop at the cap and say so.</span>
                            </p>
                        </FoldCard>

                        <FoldCard id="skills.plugs" title="Where a skill plugs in" icon={{ icon: PlugIcon, area: "setup" }} foldedFact="Create a doc">
                            <p className={cn(TYPE.tb, "m-0 text-gray-900")}>
                                Create a doc, on a client&rsquo;s Documents card, offers &ldquo;Write it with the skill&rdquo; for a doc type once its skill is here.
                            </p>
                            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Each doc type&rsquo;s slot lists what Oura sends its skill. A person reads the doc before it is shared.</p>
                            <Link to={`${OURA_ROOT}/businesses`} className={cn(TYPE.tc, "mt-3 inline-block font-medium text-blue-700 hover:underline")}>
                                Open Businesses
                            </Link>
                        </FoldCard>
                    </BentoStack>
                </Bento>
            </div>

            <Drawer open={Boolean(open)} onClose={() => setOpen(null)} nav={open?.kind === "slot" ? nav : undefined} itemKey={open ? (open.kind === "slot" ? `slot:${open.docType}` : "add") : undefined}>
                {openSlot && <SlotSheet key={openSlot.docType} slot={openSlot} cap={cap} onDone={() => setOpen(null)} />}
                {open?.kind === "add" && <AddSheet onDone={() => setOpen(null)} />}
            </Drawer>
        </FoldScope>
    );
}
